import { alpha, Box, Typography, type Theme } from '@mui/material';
import type { ComponentType } from 'react';
import type { JackpotIconType, JackpotPool, JackpotProduct } from '../../../@types';
import { useSkin } from '../../../contexts/SkinContext';
import useLocalization from '../../../hooks/useLocalization';
import { useSkinStyles } from '../../../hooks/useSkinStyles';
import { PRODUCT, SKIN, type Skin } from '../../../types/enums';
import {
  JACKPOT_WINNER_ANIMATION_SKIN_STYLES,
  JACKPOT_WINNER_TIMING_MS,
  jackpotWinnerEntrance,
} from '../../../styles/components/jackpotWinnerAnimation';
import { mergeSx } from '../../../utils/styles';
import JackpotIcon from '../../icons/JackpotIcon';
import SportsbookJackpotIcon from '../../icons/SportsbookJackpotIcon';
import JackpotPrizeAmount from './JackpotPrizeAmount';

const { card, shine } = JACKPOT_WINNER_TIMING_MS;

type TierIcon = ComponentType<{ type: JackpotIconType; size: 'medium' }>;

const tierIconBySkinAndProduct: Partial<Record<Skin, Partial<Record<JackpotProduct, TierIcon>>>> = {
  [SKIN.GOLA]: { [PRODUCT.SPORTSBOOK]: SportsbookJackpotIcon },
  [SKIN.BET365]: { [PRODUCT.SPORTSBOOK]: SportsbookJackpotIcon },
};

const styles = {
  frame: {
    ...jackpotWinnerEntrance('jackpotPrizeEnter', card.delay, card.duration),
    position: 'relative',
    zIndex: 2,
    width: 'fit-content',
    maxWidth: { xs: 'calc(100% - 32px)', md: '100%' },
    boxSizing: 'border-box',
    mt: { xs: -1, md: -2 },
    p: 0.5,
    border: '1px solid',
    borderRadius: '16px',
    // Grow from the bottom edge and keep the offset within the panel's bottom padding (16px on mobile),
    // otherwise the scrollable panel briefly shows a scrollbar while the card rises in.
    transformOrigin: 'center bottom',
    '@keyframes jackpotPrizeEnter': {
      from: { opacity: 0, transform: 'translateY(14px) scale(0.9)', filter: 'blur(8px)' },
      '55%': { opacity: 1, filter: 'blur(0)' },
      to: { opacity: 1, transform: 'translateY(0) scale(1)', filter: 'blur(0)' },
    },
  },
  surface: (theme: Theme) => ({
    position: 'relative',
    display: 'flex',
    minWidth: 0,
    boxSizing: 'border-box',
    flexDirection: 'column',
    alignItems: 'center',
    overflow: 'hidden',
    px: { xs: 4, md: 7 },
    py: { xs: 1.75, md: 2.5 },
    border: '1px solid',
    borderRadius: '12px',
    // One light sweep across the card once its contents have landed.
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      background: `linear-gradient(105deg, transparent 35%, ${alpha(theme.palette.common.white, 0.12)} 50%, transparent 65%)`,
      transform: 'translateX(-100%)',
      animation: `jackpotPrizeShine ${shine.duration}ms ease-in-out ${shine.delay}ms both`,
      '@media (prefers-reduced-motion: reduce)': { display: 'none' },
    },
    '@keyframes jackpotPrizeShine': {
      from: { transform: 'translateX(-100%)' },
      to: { transform: 'translateX(100%)' },
    },
    '@keyframes jackpotDetailEnter': {
      from: { opacity: 0, transform: 'translateY(12px)' },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
    '@keyframes jackpotDividerEnter': {
      from: { opacity: 0, transform: 'scaleX(0)' },
      to: { opacity: 1, transform: 'scaleX(1)' },
    },
  }),
  label: {
    ...jackpotWinnerEntrance('jackpotDetailEnter', JACKPOT_WINNER_TIMING_MS.heading),
    typography: 'caption',
    fontWeight: 800,
    lineHeight: '16px',
    letterSpacing: '1.5px',
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  // The same caps label, smaller and dimmer, so it sits under the heading without competing with it.
  youWon: {
    ...jackpotWinnerEntrance('jackpotDetailEnter', JACKPOT_WINNER_TIMING_MS.label),
    typography: 'caption2',
    fontWeight: 700,
    lineHeight: '12px',
    letterSpacing: '1.5px',
    textAlign: 'center',
    textTransform: 'uppercase',
    opacity: 0.65,
  },
  content: {
    display: 'flex',
    maxWidth: '100%',
    minWidth: 0,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1.25,
    pt: { xs: 1.5, md: 2 },
  },
  // Capped at the card's width so a long pool name wraps instead of pushing the coin and text out of view.
  tier: {
    ...jackpotWinnerEntrance('jackpotDetailEnter', JACKPOT_WINNER_TIMING_MS.tier),
    display: 'flex',
    minWidth: 0,
    maxWidth: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1.25,
  },
  tierIcon: {
    display: 'flex',
    flexShrink: 0,
    width: 30,
    height: 30,
  },
  // Long names wrap onto a second line (never more) rather than being cut short.
  tierName: {
    display: '-webkit-box',
    minWidth: 0,
    overflow: 'hidden',
    typography: { xs: 'h3', md: 'h2' },
    fontWeight: { xs: 700, md: 700 },
    textAlign: 'center',
    overflowWrap: 'anywhere',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
  },
  divider: {
    ...jackpotWinnerEntrance('jackpotDividerEnter', JACKPOT_WINNER_TIMING_MS.divider),
    width: '100%',
    height: '1px',
    flexShrink: 0,
  },
} as const;

const JackpotPrizeCard = ({ winningJackpotPool }: { winningJackpotPool: JackpotPool }) => {
  const { t } = useLocalization();
  const { skin } = useSkin();
  const skinStyles = useSkinStyles(JACKPOT_WINNER_ANIMATION_SKIN_STYLES);
  const TierIcon = tierIconBySkinAndProduct[skin]?.[winningJackpotPool.product] ?? JackpotIcon;

  return (
    <Box sx={mergeSx(styles.frame, skinStyles.prizeFrame)} data-testid='jackpot-winner-prize'>
      <Box sx={mergeSx(styles.surface, skinStyles.prizeSurface)} data-testid='jackpot-prize-surface'>
        <Typography sx={mergeSx(styles.label, skinStyles.label)}>{t('congratulations', 'Congratulations!')}</Typography>
        <Typography sx={mergeSx(styles.youWon, skinStyles.label)}>{t('youWon', 'You Won')}</Typography>
        <Box sx={styles.content} data-testid='jackpot-prize-content'>
          <Box sx={styles.tier}>
            <Box sx={styles.tierIcon}>
              <TierIcon type={winningJackpotPool.icon} size='medium' />
            </Box>
            <Typography sx={mergeSx(styles.tierName, skinStyles.tierName)}>{winningJackpotPool.name}</Typography>
          </Box>

          <Box
            aria-hidden='true'
            sx={mergeSx(styles.divider, skinStyles.divider)}
            data-testid='jackpot-prize-divider'
          />

          <JackpotPrizeAmount amount={winningJackpotPool.amount} />
        </Box>
      </Box>
    </Box>
  );
};

export default JackpotPrizeCard;
